import cx from 'classnames';

import styles from './BaseEditorTheme.module.css';
import code from './CodeTheme.module.css';
import typo from './EditorTypography.module.css';
import table from './TableTheme.module.css';

export type EditorTextSize = 'large' | 'medium' | 'small';
export type EditorTextColor = 'standard' | 'subtle' | 'inherit';
export type EditorProfile = 'default' | 'paper';

export type EditorTypographyOptions = {
  textSize?: EditorTextSize;
  textColor?: EditorTextColor;
  textBold?: boolean;
  profile?: EditorProfile;
};

const sizeClass: Record<EditorTextSize, string> = {
  large: typo.sizeLarge,
  medium: typo.sizeMedium,
  small: typo.sizeSmall,
};

const colorClass: Record<EditorTextColor, string> = {
  standard: typo.colorStandard,
  subtle: typo.colorSubtle,
  inherit: typo.colorInherit,
};

export default function getEditorTheme({
  textSize = 'medium',
  textColor = 'standard',
  textBold = false,
  profile = 'default',
}: EditorTypographyOptions = {}) {
  const isPaper = profile === 'paper';

  const bodySize = isPaper ? null : sizeClass[textSize];
  const bodyColor = colorClass[textColor];

  const paragraph = isPaper
    ? cx(typo.paperBody, bodyColor, textBold && typo.paperBodyAllBold)
    : cx(bodySize, bodyColor, textBold && typo.allBold);

  const listitem = cx(paragraph, styles.li);

  const headingDefault = (): string => {
    if (textSize === 'large') {
      return cx(typo.headingTitleSmall, bodyColor);
    }
    if (textSize === 'small') {
      return cx(typo.headingSmall, bodyColor);
    }
    return cx(typo.headingMedium, bodyColor);
  };

  const headingPaper = (): {h1: string; h2: string; h3: string; h4: string; h5: string} => {
    const h1 = cx(typo.paperH1, bodyColor);
    const h2 = cx(typo.paperH2, bodyColor);
    const h3 = cx(typo.paperH3, bodyColor);
    return {
      h1,
      h2,
      h3,
      h4: h1,
      h5: h1,
    };
  };

  const heading =
    profile === 'paper'
      ? headingPaper()
      : {
          h1: headingDefault(),
          h2: headingDefault(),
          h3: headingDefault(),
          h4: headingDefault(),
          h5: headingDefault(),
        };

  const boldExtra =
    profile === 'paper'
      ? typo.boldPaperLine
      : textSize === 'small' && textColor === 'subtle'
        ? typo.boldSmallSubtle
        : undefined;

  return {
    placeholder: styles['editor-placeholder'],
    paragraph,
    // Paper-profile documents (news posts, Paper pastes) show quotes as quotes; elsewhere they
    // stay paragraph-styled as before.
    quote: isPaper ? cx(paragraph, styles.quote) : paragraph,
    blockquote: isPaper ? cx(paragraph, styles.quote) : paragraph,
    hr: styles.hr,
    heading,
    table: isPaper ? table.table : styles.table,
    tr: table.tr,
    tableCell: table.td,
    tableCellHeader: table.tableCellHeader,
    tableCellResizer: table.tableCellResizer,
    tableCellSelected: table.tableCellSelected,
    tableScrollableWrapper: table.tableScrollableWrapper,
    tableSelected: table.tableSelected,
    tableSelection: table.tableSelection,
    list: {
      nested: {
        listitem: styles.nestedList,
      },
      olDepth: [styles.ol1, styles.ol2, styles.ol3],
      ulDepth: [styles.ul1, styles.ul2, styles.ul3],
      ol: styles.ol,
      ul: styles.ul,
      listitem,
      listitemChecked: styles.listItemChecked,
      listitemUnchecked: styles.listItemUnchecked,
    },
    link: styles.link,
    text: {
      bold: cx(styles.bold, boldExtra),
      italic: styles.italic,
      underline: styles.underline,
      strikethrough: styles.strikethrough,
      underlineStrikethrough: styles.underlineStrikethrough,
      highlight: styles.highlight,
      code: code['editor-text-code'],
    },
    code: code['editor-code'],
    mark: styles.mark,
    markOverlap: styles.markOverlap,
    codeHighlight: {
      atrule: code['editor-tokenAttr'],
      attr: code['editor-tokenAttr'],
      boolean: code['editor-tokenProperty'],
      builtin: code['editor-tokenSelector'],
      cdata: code['editor-tokenComment'],
      char: code['editor-tokenSelector'],
      class: code['editor-tokenFunction'],
      comment: code['editor-tokenComment'],
      constant: code['editor-tokenProperty'],
      deleted: code['editor-tokenProperty'],
      doctype: code['editor-tokenComment'],
      entity: code['editor-tokenOperator'],
      function: code['editor-tokenFunction'],
      important: styles['editor-tokenVariable'],
      inserted: code['editor-tokenSelector'],
      keyword: code['editor-tokenAttr'],
      namespace: styles['editor-tokenVariable'],
      number: code['editor-tokenProperty'],
      operator: code['editor-tokenOperator'],
      prolog: code['editor-tokenComment'],
      property: code['editor-tokenProperty'],
      punctuation: styles['editor-tokenPunctuation'],
      regex: code['editor-tokenVariable'],
      selector: code['editor-tokenSelector'],
      string: code['editor-tokenSelector'],
      symbol: code['editor-tokenProperty'],
      tag: code['editor-tokenProperty'],
      url: code['editor-tokenOperator'],
      variable: code['editor-tokenVariable'],
    },
  };
}
